import axios from "axios";

export const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const resolveAssetUrl = (value = "") => {
  if (!value || /^(?:https?:|data:|blob:)/i.test(value)) return value;
  if (value.startsWith("/api/")) return `${backendUrl}${value}`;
  return value;
};

const api = axios.create({
  baseURL: `${backendUrl}/api`,
  withCredentials: true,
  timeout: 90000,
});

export const SESSION_EXPIRED_EVENT = "netzer-session-expired";
const authTransport = axios.create({ baseURL: `${backendUrl}/api`, withCredentials: true, timeout: 90000 });
let refreshPromise = null;
let sessionRevision = 0;
const authActions = new Set(["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"]);

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = authTransport.post("/auth/refresh").then((res) => {
      // A hosting wake-up page can return HTML with status 200. It is not a renewed session.
      if (res.data?.ok !== true) throw new Error("O servidor ainda está iniciando. Tente novamente.");
      sessionRevision += 1;
    }).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// Logout waits for an already-running renewal before clearing the cookies.
export const waitForSessionRefresh = () => refreshPromise?.catch(() => {}) || Promise.resolve();

api.interceptors.request.use((config) => {
  config._sessionRevision = sessionRevision;
  return config;
});

api.interceptors.response.use(
  (res) => {
    if (authActions.has(res.config.url)) sessionRevision += 1;
    return res;
  },
  async (err) => {
    const config = err.config;
    if (!config || err.response?.status !== 401 || authActions.has(config.url) || config.url?.startsWith("/public/")) {
      return Promise.reject(err);
    }
    if (config._authRetry) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      return Promise.reject(err);
    }
    config._authRetry = true;
    try {
      // Concurrent 401s share one renewal, including responses arriving just after it finishes.
      if (config._sessionRevision === sessionRevision) await refreshSession();
    } catch (refreshError) {
      if (refreshError.response?.status === 401) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      // A network failure is not a logout. Do not replay writes after timeouts or 5xx responses.
      return Promise.reject(refreshError);
    }
    return api(config);
  }
);

export const apiError = (e) => {
  const d = e.response?.data?.detail;
  if (!d) return !e.response ? "Não foi possível conectar ao servidor. Confira a conexão e tente novamente." : "Algo deu errado. Tente novamente.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return String(d);
};

export default api;
